import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  CssBaseline,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../middleware/auth0.service';
import {
  addBarberToShop,
  addBusinessHoursToShopBarber,
  updateShopBarberUserId,
} from '../../middleware/registration_service/updateRequest';
import { ShopBarber } from '../../types/shop';
import { hours } from '../../defaults/BusinessHours';
import headerLogo from '../../images/header-logo.png';
import {
  darkPageBg,
  darkFormContainerSx,
  darkCardBg,
  darkTextFieldSx,
  darkSelectSx,
  darkSelectMenuProps,
  darkButtonSx,
  darkOutlinedButtonSx,
  accent,
} from '../components/darkFormStyles';

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Dr', 'Prof'];

interface BarberForm {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  confirmPassword: string;
}

const emptyBarberForm = (): BarberForm => ({
  title: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  password: '',
  confirmPassword: '',
});

const ShopBarberRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addedBarbers, setAddedBarbers] = useState<string[]>([]);
  const [addSelf, setAddSelf] = useState(false);
  const [selfAdded, setSelfAdded] = useState(false);
  const [form, setForm] = useState<BarberForm>(emptyBarberForm());

  const shopId = localStorage.getItem('reg_shop_id') || '';
  const shopOwnerAuth0Id = localStorage.getItem('reg_auth0_user_id') || '';

  // Pre-fill self data if checkbox is ticked
  const handleAddSelfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddSelf(e.target.checked);
    if (e.target.checked) {
      // We'll use stored registration data for the owner
      setForm({
        ...form,
        email: localStorage.getItem('reg_owner_email') || '',
        firstName: localStorage.getItem('reg_owner_firstName') || '',
        lastName: localStorage.getItem('reg_owner_lastName') || '',
        title: localStorage.getItem('reg_owner_title') || '',
        phoneNumber: localStorage.getItem('reg_owner_phone') || '',
        password: '',
        confirmPassword: '',
      });
    } else {
      setForm(emptyBarberForm());
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleTitleChange = (e: SelectChangeEvent) => {
    setForm({ ...form, title: e.target.value });
  };

  const registerBarber = async (isSelf: boolean) => {
    setError(null);

    if (!isSelf && form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!shopId) {
      setError('Shop ID not found. Please start registration again.');
      return;
    }

    if (
      !form.title ||
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      (!isSelf && !form.password)
    ) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      const shopBarberData: ShopBarber = {
        title: form.title,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        shopId: BigInt(shopId),
        userId: isSelf ? shopOwnerAuth0Id : '',
      };

      const res = await addBarberToShop(shopBarberData, BigInt(shopId));
      const shopBarberId: bigint = res.data.shopBarbers?.find(
        (sb) =>
          sb.email === form.email &&
          sb.firstName === form.firstName &&
          sb.lastName === form.lastName
      )?.id;

      if (shopBarberId) {
        await addBusinessHoursToShopBarber(hours, shopBarberId);
      } else {
        setError('Failed to retrieve new barber ID. Please try again.');
        return;
      }

      if (isSelf) {
        // If adding self, update with existing Auth0 user id
        try {
          await updateShopBarberUserId(shopBarberId, shopOwnerAuth0Id);
        } catch (e) {
          setError('Failed to link your Auth0 account. Please try again.');
        }
        setSelfAdded(true);
        setAddedBarbers((prev) => [
          ...prev,
          `${form.firstName} ${form.lastName} (You)`,
        ]);
      } else {
        // Sign up new barber in Auth0
        await new Promise<void>((resolve, reject) => {
          auth.signup(
            {
              email: form.email,
              password: form.password,
              connection: 'Username-Password-Authentication',
              userMetadata: {
                firstName: form.firstName,
                lastName: form.lastName,
              },
            },
            async (err: any, result: any) => {
              if (err) {
                reject(
                  new Error(
                    err.code === 'invalid_signup'
                      ? 'This email already exists.'
                      : err.code === 'invalid_password'
                      ? 'Password is not strong enough.'
                      : err.description ||
                        err.code ||
                        'Auth0 registration failed.'
                  )
                );
                return;
              }
              const auth0UserId = result.Id || result._id || result.id || '';
              try {
                await updateShopBarberUserId(shopBarberId, String(auth0UserId));
              } catch (e) {
                setError(
                  'Failed to update shop barber with Auth0 User ID. Please try again.'
                );
              }
              resolve();
            }
          );
        });
        setAddedBarbers((prev) => [
          ...prev,
          `${form.firstName} ${form.lastName}`,
        ]);
      }

      setForm(emptyBarberForm());
      setAddSelf(false);
    } catch (err: any) {
      setError(
        err?.message ||
          err?.response?.data?.message ||
          'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    navigate('/register/subscription');
  };

  const canAddMore = addedBarbers.length < 2;

  return (
    <Box sx={{ bgcolor: darkPageBg, minHeight: '100vh', py: 8 }}>
      <Container
        component="main"
        maxWidth="sm"
        sx={{ ...darkFormContainerSx, mt: 4, mb: 4, p: 4 }}
      >
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={headerLogo}
            alt="Barbers Limited"
            sx={{ width: 88, height: 88, objectFit: 'contain', mb: 1 }}
          />
          <Typography
            component="h1"
            variant="h5"
            fontWeight="bold"
            sx={{ color: '#fff' }}
          >
            Add Shop Barbers
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1, textAlign: 'center', color: 'rgba(255,255,255,0.65)' }}
          >
            Step 3 of 3 — Add up to 2 shop barbers
          </Typography>

          {/* Added barbers summary */}
          {addedBarbers.length > 0 && (
            <Box sx={{ mt: 2, width: '100%' }}>
              <Typography
                variant="subtitle2"
                sx={{ color: 'rgba(255,255,255,0.65)' }}
                gutterBottom
              >
                Added Barbers ({addedBarbers.length}/2):
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {addedBarbers.map((name, i) => (
                  <Chip
                    key={i}
                    label={name}
                    color="success"
                    icon={<CheckCircleIcon />}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          {canAddMore ? (
            <Box sx={{ mt: 3, width: '100%' }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: darkCardBg,
                  borderColor: 'rgba(255,255,255,0.08)',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <PersonAddIcon sx={{ color: accent }} />
                  <Typography variant="h6" sx={{ color: '#fff' }}>
                    {addedBarbers.length === 0
                      ? 'Add First Barber'
                      : 'Add Second Barber'}
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addSelf}
                      onChange={handleAddSelfChange}
                      disabled={selfAdded}
                      sx={{
                        color: 'rgba(255,255,255,0.5)',
                        '&.Mui-checked': { color: accent },
                      }}
                    />
                  }
                  label="Add myself as a shop barber"
                  sx={{ mb: 2, color: 'rgba(255,255,255,0.75)' }}
                />
                <Divider
                  sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.12)' }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required sx={darkSelectSx}>
                      <InputLabel id="barber-title-label">Title</InputLabel>
                      <Select
                        labelId="barber-title-label"
                        value={form.title}
                        label="Title"
                        onChange={handleTitleChange}
                        MenuProps={darkSelectMenuProps}
                      >
                        {TITLE_OPTIONS.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  {!addSelf && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          label="Password"
                          name="password"
                          type="password"
                          value={form.password}
                          onChange={handleChange}
                          sx={darkTextFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          label="Confirm Password"
                          name="confirmPassword"
                          type="password"
                          value={form.confirmPassword}
                          onChange={handleChange}
                          sx={darkTextFieldSx}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Button
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  onClick={() => registerBarber(addSelf)}
                  sx={{ mt: 3, py: 1.5, ...darkButtonSx }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Add Barber'
                  )}
                </Button>
              </Paper>
            </Box>
          ) : (
            <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
              You've added the maximum of 2 shop barbers.
            </Alert>
          )}

          <Divider
            sx={{ my: 3, width: '100%', borderColor: 'rgba(255,255,255,0.12)' }}
          />

          <Button
            fullWidth
            variant="outlined"
            onClick={handleFinish}
            disabled={addedBarbers.length === 0}
            sx={{ py: 1.5, ...darkOutlinedButtonSx }}
          >
            {addedBarbers.length === 0
              ? 'Add at least one barber to finish'
              : 'Select Plan & Go to Login'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ShopBarberRegistration;
